import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { RootState } from "./store"

export type WorkflowZone =
  | { kind: "country"; country: string }
  | { kind: "circle"; lat: number; lng: number; radius: number }

export type WorkflowSchedule = {
  /** "cron" (recurring), "every" (interval), "at" (one-shot), or "event" (geo-trigger). */
  kind: string
  /** Cron expression (5-field) when kind === "cron". */
  expr?: string
  /** Interval in milliseconds when kind === "every". */
  everyMs?: number
  /** IANA timezone (only with kind === "cron"). */
  tz?: string
  /** ISO-8601 timestamp when kind === "at" (fires once, then auto-pauses). */
  ts?: string
  /** Event name when kind === "event" (e.g. "vehicle.entered_zone", "vehicle.exited_zone"). */
  on?: string
  /** Zone descriptor for event-based schedules. */
  zone?: WorkflowZone
  /** Route stop this rule is pinned to (stop-event schedules). */
  stopId?: string
  /** Stop type filter for stop-event schedules. */
  stopAction?: "pickup" | "delivery"
  /** Driver JID or vehicle room JID the rule is limited to. */
  targetJid?: string
  /** Minutes at a stop before a stop-dwell rule fires. */
  minDwellMin?: number
  /** Document type filter for document-check schedules (e.g. "CMR"). */
  documentType?: string
  /** Document-check rule fires only when the reading found something to fix. */
  onlyWithIssues?: boolean
}

// ─── fleet-reminder types ────────────────────────────────────────────────────

export type FleetReminderDue =
  | { kind: "yearly"; month: number; day: number }
  | { kind: "date"; date: string }

export type FleetReminderFired = {
  dueDate: string
  advances: number[]
  acceptance?: { acceptedAt: string }
}

export type FleetReminderEntry = {
  plate: string
  status: "active" | "inactive"
  due: FleetReminderDue
  fired?: FleetReminderFired
}

// ─── union ───────────────────────────────────────────────────────────────────

type WorkflowBase = {
  id: string
  name: string
  scope: "global" | "local"
  author: string
}

export type ScheduledWorkflow = WorkflowBase & {
  kind: "cron"
  status: "active" | "paused"
  schedule: WorkflowSchedule | null
  /**
   * The agent-turn payload: an instruction for the agent, not the literal text
   * sent to the driver. Kept so an editor can prefill what is already set.
   */
  message?: string
  /**
   * Payload kind. A "handler" workflow is a built-in job (a brief, a fuel
   * monitor) whose behaviour lives in the agent, not in a prompt — sending it
   * a `message` on update would overwrite that handler with an agent turn.
   */
  payloadKind?: string
}

export type FleetReminderWorkflow = WorkflowBase & {
  kind: "fleet-reminder"
  status: "active" | "inactive"
  advance: number[]
  action?: { kind: string; message: string }
  entries: FleetReminderEntry[]
}

export type WorkflowJob = ScheduledWorkflow | FleetReminderWorkflow

// ─── slice ───────────────────────────────────────────────────────────────────

type WorkflowsState = Record<string, WorkflowJob>

const initialState: WorkflowsState = {}

const openclawWorkflowsSlice = createSlice({
  name: "openclawWorkflows",
  initialState,
  reducers: {
    /** Replace entire state — used on login */
    setAllWorkflows: (
      _state,
      action: PayloadAction<{ workflows: WorkflowJob[] }>,
    ) => {
      const next: WorkflowsState = {}
      action.payload.workflows.forEach((job) => {
        next[job.id] = job
      })
      return next
    },

    upsertItemWorkflows: (
      state,
      action: PayloadAction<{ itemId: string; workflows: WorkflowJob[] }>,
    ) => {
      const { itemId, workflows } = action.payload
      for (const id of Object.keys(state)) {
        if (state[id].author === itemId) delete state[id]
      }
      workflows.forEach((job) => {
        state[job.id] = job
      })
    },

    removeItemWorkflows: (state, action: PayloadAction<{ itemId: string }>) => {
      const { itemId } = action.payload
      for (const id of Object.keys(state)) {
        if (state[id].author === itemId) delete state[id]
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const { setAllWorkflows, upsertItemWorkflows, removeItemWorkflows } =
  openclawWorkflowsSlice.actions

// Object.values() always returns a new array, so an unmemoized version of
// this re-renders every subscriber on every dispatch (this app's store gets
// very frequent unrelated updates — live GPS/route sync). createSelector
// keeps the same array reference as long as state.openclawWorkflows itself
// hasn't changed.
export const selectAllWorkflows = createSelector(
  (state: RootState) => state.openclawWorkflows,
  (openclawWorkflows): WorkflowJob[] => Object.values(openclawWorkflows),
)

export default openclawWorkflowsSlice.reducer
